import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Plus, ChevronDown } from 'lucide-react';
import { BusinessHoursData, Break } from '../types';
import { Tooltip } from './Tooltip';
import { BreakFormModal } from './BreakFormModal';

interface DayHoursProps {
  dayName: string;
  hours: BusinessHoursData;
  breaks: Break[];
  onHoursChange: (field: string, value: any) => void;
  onAddBreak: (startTime: string, endTime: string) => void;
  onDeleteBreak: (id: string) => void;
}

export function DayHours({
  dayName,
  hours,
  breaks,
  onHoursChange,
  onAddBreak,
  onDeleteBreak
}: DayHoursProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);
  const [modalStartTime, setModalStartTime] = useState("");
  const [modalEndTime, setModalEndTime] = useState("");

  const isActive = hours.isActive ?? false;
  const startTime = hours.startTime || '09:00';
  const endTime = hours.endTime || '17:00';

  const calculateBreakPercentage = (breakStart: string, breakEnd: string, dayStart: string, dayEnd: string): { start: number; height: number } => {
    const toMinutes = (time: string) => {
      if (!time) return 0; // Prevent .split() on undefined/null/empty
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const dayStartMinutes = toMinutes(dayStart);
    const dayEndMinutes = toMinutes(dayEnd);
    const breakStartMinutes = toMinutes(breakStart);
    const breakEndMinutes = toMinutes(breakEnd);

    const dayLength = dayEndMinutes - dayStartMinutes;
    const startPercentage = ((breakStartMinutes - dayStartMinutes) / dayLength) * 100;
    const heightPercentage = ((breakEndMinutes - breakStartMinutes) / dayLength) * 100;
    
    return {
      start: startPercentage,
      height: heightPercentage
    };
  };

  const calculateBreakDuration = (start: string, end: string): string => {
    if (!start || !end) return ""; // Prevent .split() on undefined
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);
    
    let totalMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
    if (totalMinutes < 0) totalMinutes += 24 * 60;
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) return `${minutes} דקות`;
    if (minutes === 0) return `${hours} שעות`;
    return `${hours} שעות ו-${minutes} דקות`;
  };

  function formatTime(time?: string) {
    if (!time) return ""; // Prevent .slice() on undefined
    return time.slice(0, 5);
  }

  const openBreakModal = () => {
    setModalStartTime("");
    setModalEndTime("");
    setIsBreakModalOpen(true);
  };

  const closeBreakModal = () => {
    setIsBreakModalOpen(false);
  };

  const handleAddBreak = (startTime: string, endTime: string) => {
    onAddBreak(startTime, endTime);
    setIsBreakModalOpen(false);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center justify-between flex-1">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isActive}
                  onChange={(e) => onHoursChange('isActive', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <div>
                <h3 className="font-medium text-gray-900">{dayName}</h3>
                {isActive && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {startTime} - {endTime}
                  </p>
                )}
              </div>
            </div>
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-400 active:text-gray-600"
              disabled={!isActive}
            >
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="h-5 w-5" />
              </motion.div>
            </button>
          </div>

          {/* Desktop Time Inputs */}
          <div className="hidden sm:flex items-center gap-4">
            {isActive && (
              <div className="flex items-center gap-2">
                <Tooltip content="שעת פתיחה">
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => onHoursChange('startTime', e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </Tooltip>
                <span className="text-gray-500">עד</span>
                <Tooltip content="שעת סגירה">
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => onHoursChange('endTime', e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && isActive && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-100"
            >
              {/* Mobile Time Inputs */}
              <div className="p-4 sm:hidden bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      שעת פתיחה
                    </label>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => onHoursChange('startTime', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      שעת סגירה
                    </label>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => onHoursChange('endTime', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Breaks Section */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-700">הפסקות</h4>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={openBreakModal}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 active:text-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    הוסף
                  </motion.button>
                </div>

                <div className="space-y-2">
                  {breaks.map((breakItem) => (
                    <motion.div
                      key={breakItem.id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="bg-gray-50 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {`${formatTime(breakItem.start_time || breakItem.startTime) || "--:--"} - ${formatTime(breakItem.end_time || breakItem.endTime) || "--:--"}`}
                          </div>
                          <div className="text-xs text-gray-500">
                            {calculateBreakDuration(breakItem.start_time || breakItem.startTime, breakItem.end_time || breakItem.endTime)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => onDeleteBreak(breakItem.id)}
                        className="text-sm text-red-600 active:text-red-700"
                      >
                        הסר
                      </button>
                    </motion.div>
                  ))}

                  {breaks.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-500">
                        לא הוגדרו הפסקות ליום זה
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Break Modal */}
      {isBreakModalOpen && (
        <BreakFormModal
          startTime={modalStartTime}
          endTime={modalEndTime}
          onStartTimeChange={setModalStartTime}
          onEndTimeChange={setModalEndTime}
          onClose={closeBreakModal}
          onSubmit={handleAddBreak}
        />
      )}
    </>
  );
}